import { locationGroupApi } from '@/api/account-management/locationGroupApi';
import { tipConfigurationApi } from '@/api/account-management/tipConfigurationApi';
import { type Location } from '@/models/account-management/Location';
import { type LocationGroup } from '@/models/account-management/LocationGroup';
import { TipCalculationBasis, TipConfigurationScope, type TipConfiguration } from '@/models/account-management/TipConfiguration';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import Select from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useAppContext } from '../../AppContext';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import { usePermissions } from '../../permissions/usePermissions';
import { Perm } from '../../permissions/permissions';
import { fillHeight } from '../../theme';
import { useAlert } from '../common/alert/AlertProvider';
import Spacer from '../common/Spacer';
import { useToast } from '../common/toast/ToastProvider';
import { useAssignedLocations } from '../../permissions/useLocationScope';

const num = (v: string) => Number(v) || 0;
// Render 0 as an empty field so typing doesn't leave a leading "0" prepended.
const disp = (n: number): number | '' => (n === 0 ? '' : n);

const Tips = () => {
    const qc = useQueryClient();
    const toast = useToast();
    const alert = useAlert();
    const strings = useLocalizedStrings();
    const app = useAppContext();
    const perms = usePermissions();
    const canUpdate = perms.canUpdate(Perm.configurationTips);
    const canDelete = perms.canDelete(Perm.configurationTips);

    const brandId = app.brandId ?? undefined;

    const [level, setLevel] = useState<TipConfigurationScope>(TipConfigurationScope.Brand);
    const [groupId, setGroupId] = useState('');
    const [locationId, setLocationId] = useState('');
    const [calculationBasis, setCalculationBasis] = useState<TipCalculationBasis>(TipCalculationBasis.Subtotal);

    // For Brand level the target is the brand itself; otherwise it's the chosen group/location.
    const scopeId =
        level === TipConfigurationScope.Brand ? brandId :
        level === TipConfigurationScope.LocationGroup ? (groupId || undefined) :
        (locationId || undefined);

    const { data: groups } = useQuery<LocationGroup[]>({ queryKey: ['location-groups', brandId ?? ''], enabled: !!brandId, queryFn: () => locationGroupApi.list(brandId!), staleTime: Infinity });
    // Only the locations this user is assigned to - the tip scope picker offered the brand's whole estate (TTD-160).
    const { locations } = useAssignedLocations(brandId);

    const { data: config } = useQuery<TipConfiguration | null>({
        queryKey: ['tip-config', level, scopeId ?? ''],
        enabled: !!scopeId,
        queryFn: () => tipConfigurationApi.get(level, scopeId!),
        staleTime: 0,
    });

    const [tip1, setTip1] = useState(0);
    const [tip2, setTip2] = useState(0);
    const [tip3, setTip3] = useState(0);
    const [defaultPreset, setDefaultPreset] = useState(1); // which of tip1/2/3 is the default
    const [guestCountMinimumTip, setGuestCountMinimumTip] = useState(0);
    const [minimumTipAmount, setMinimumTipAmount] = useState(0);

    // Three states, not two. Null is "inherit from the scope above" and is what an untouched scope stores -
    // see the model. A brand has nothing above it, so it only ever offers on or off.
    const [tipsEnabled, setTipsEnabled] = useState<boolean | null>(null);

    // Seed the form from the loaded config for the current scope (zeros when nothing is set).
    useEffect(() => {
        const c = config ?? null;
        setCalculationBasis(c?.calculationBasis ?? TipCalculationBasis.Subtotal);
        setTip1(c?.tipAmount1 ?? 0);
        setTip2(c?.tipAmount2 ?? 0);
        setTip3(c?.tipAmount3 ?? 0);
        setGuestCountMinimumTip(c?.guestCountMinimumTip ?? 0);
        setMinimumTipAmount(c?.minimumTipAmount ?? 0);

        // ?? null, not ?? true: a scope with no row has stated nothing, and defaulting the control to "on"
        // would write an explicit policy the moment anything else on the form was saved.
        setTipsEnabled(c?.tipsEnabled ?? null);
        const dta = c?.defaultTipAmount ?? 0;
        setDefaultPreset(dta !== 0 && dta === c?.tipAmount3 ? 3 : dta !== 0 && dta === c?.tipAmount2 ? 2 : 1);
    }, [config, scopeId, level]);

    const invalidate = () => qc.invalidateQueries({ queryKey: ['tip-config', level, scopeId ?? ''] });

    const { mutateAsync: save, isPending: saving } = useMutation({
        mutationFn: async () => {
            const dto: TipConfiguration = {
                id: config?.id,
                brandId: brandId!,
                scopeType: level,
                scopeId: scopeId!,
                calculationBasis,
                tipAmount1: tip1,
                tipAmount2: tip2,
                tipAmount3: tip3,
                defaultTipAmount: defaultPreset === 3 ? tip3 : defaultPreset === 2 ? tip2 : tip1,
                guestCountMinimumTip,
                minimumTipAmount,

                // A brand cannot inherit — there is nothing above it — so an untouched brand saves an explicit
                // true rather than null. Otherwise the top of the chain would never state anything, and a
                // client who turned tips off and back on again would find the control had nothing to show.
                tipsEnabled: level === TipConfigurationScope.Brand ? (tipsEnabled ?? true) : tipsEnabled,
            };
            await tipConfigurationApi.upsert(dto);
        },
        onSuccess: async () => { toast({ severity: 'success', message: strings.tipConfigSaved }); await invalidate(); },

        // Show what the server said, not just that something went wrong. A bare "failed to save" gives whoever
        // hits it nothing to act on and nothing to report - the server already explains itself, and every
        // other editor in here surfaces that.
        onError: (e: unknown) => {
            const body = (e as { body?: string })?.body;
            toast({
                severity: 'error',
                message: typeof body === 'string' && body ? body : strings.tipConfigSaveError,
            });
        },
    });

    const { mutateAsync: removeConfig } = useMutation({
        mutationFn: () => tipConfigurationApi.remove(level, scopeId!),
        onSuccess: async () => { toast({ severity: 'success', message: strings.tipConfigDeleted }); await invalidate(); },
        onError: () => toast({ severity: 'error', message: strings.tipConfigDeleteError }),
    });

    const confirmDelete = async () => {
        const agree = await alert({
            title: strings.confirmDeleteTitle,
            message: strings.confirmDeleteMessage,
            destructive: true,
            agreeButtonTitle: strings.delete,
            closeButtonTitle: strings.cancel,
        });
        if (agree) await removeConfig();
    };

    if (!brandId) {
        return (
            <Box sx={{ ...fillHeight, p: 2 }}>
                <Typography variant="body2" color="text.secondary">{strings.feedbackSelectBrandTopBar}</Typography>
            </Box>
        );
    }

    const presets = [
        { idx: 1, label: strings.tipAmount1, value: tip1, set: setTip1 },
        { idx: 2, label: strings.tipAmount2, value: tip2, set: setTip2 },
        { idx: 3, label: strings.tipAmount3, value: tip3, set: setTip3 },
    ];

    // Large-party auto tip: setting a Party Size turns the feature on, so the % Amount becomes required.
    // With no party size the % doesn't apply, so the field is disabled (and forced to 0).
    const partyTipInvalid = guestCountMinimumTip > 0 && minimumTipAmount <= 0;

    return (
        <Box sx={{ ...fillHeight, overflow: 'auto', p: 2 }}>
            <Spacer y={1} />

            <Stack direction="row" spacing={2} alignItems="center">
                <FormControl size="input" sx={{ minWidth: 220 }}>
                    <InputLabel id="tip-level-label">{strings.tipLevel}</InputLabel>
                    <Select
                        labelId="tip-level-label"
                        label={strings.tipLevel}
                        size="small"
                        value={level}
                        onChange={(e) => setLevel(Number(e.target.value) as TipConfigurationScope)}
                    >
                        <MenuItem value={TipConfigurationScope.Brand}>{strings.tipLevelBrand}</MenuItem>
                        {/* Location Group tip scope TEMPORARILY DISABLED: keeping group-scoped tips in sync with the
                            CheckPlease tempdb across membership/priority changes is too fragile. Re-enable when
                            AccountManagement.TipConfiguration is the single source of truth (tempdb gone). */}
                        {/* <MenuItem value={TipConfigurationScope.LocationGroup}>{strings.tipLevelGroup}</MenuItem> */}
                        <MenuItem value={TipConfigurationScope.Location}>{strings.tipLevelLocation}</MenuItem>
                    </Select>
                </FormControl>

                {level === TipConfigurationScope.LocationGroup && (
                    <FormControl size="input" sx={{ minWidth: 280 }}>
                        <InputLabel id="tip-group-label" sx={{ marginTop: '-8px' }}>{strings.tipSelectGroup}</InputLabel>
                        <Select labelId="tip-group-label" size="small" label={strings.tipSelectGroup} value={groupId} onChange={(e) => setGroupId(e.target.value)}>
                            {(groups ?? []).map((g) => (
                                <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                {level === TipConfigurationScope.Location && (
                    <FormControl size="input" sx={{ minWidth: 280 }}>
                        <InputLabel id="tip-location-label">{strings.tipSelectLocation}</InputLabel>
                        <Select labelId="tip-location-label" size="small" label={strings.tipSelectLocation} value={locationId} onChange={(e) => setLocationId(e.target.value)}>
                            {(locations ?? []).map((l) => (
                                <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
            </Stack>

            <Spacer y={2} />

            {!scopeId ? (
                <Typography variant="body2" color="text.secondary">{strings.tipSelectTargetPrompt}</Typography>
            ) : (
                <Box>
                    {config === null && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{strings.tipNoConfigForScope}</Typography>
                    )}

                    <Typography variant="subtitle1" fontWeight={700}>{strings.tips}</Typography>

                    {/*
                      * Off is the only state this control states outright.
                      *
                      * On stores null below a brand rather than true - null means "this scope does not
                      * object", so a brand that later turns tipping off still reaches locations whose presets
                      * somebody has edited. Storing true here would opt each of them out of the brand switch
                      * permanently, which is the failure this whole field was shaped to avoid. A brand has
                      * nothing above it, so there On is a plain true.
                      */}
                    <FormControlLabel
                        sx={{ mt: 1, mb: 0.5 }}
                        control={
                            <Switch
                                checked={tipsEnabled !== false}
                                onChange={(e) => setTipsEnabled(
                                    e.target.checked
                                        ? (level === TipConfigurationScope.Brand ? true : null)
                                        : false)}
                            />
                        }
                        label={strings.tipsEnabled}
                    />
                    <FormHelperText sx={{ mb: 2 }}>
                        {tipsEnabled === false ? strings.tipsEnabledOffHelp : ' '}
                    </FormHelperText>

                    {/* Everything below configures how a tip is asked for, so none of it means anything when
                        the answer is that one is not. Hidden rather than disabled: a greyed-out form still
                        reads as something to fill in. The values are kept and saved untouched, so turning
                        tipping back on restores what was there. */}
                    {tipsEnabled !== false && (
                        <>
                            <FormControl size="small" sx={{ minWidth: 220, mt: 1, mb: 2 }}>
                                <InputLabel id="tip-calculation-basis-label">{strings.tipCalculationBasis}</InputLabel>
                                <Select
                                    labelId="tip-calculation-basis-label"
                                    label={strings.tipCalculationBasis}
                                    value={calculationBasis}
                                    onChange={(e) => setCalculationBasis(Number(e.target.value) as TipCalculationBasis)}
                                >
                                    <MenuItem value={TipCalculationBasis.Subtotal}>{strings.tipCalculationBasisSubtotal}</MenuItem>
                                    <MenuItem value={TipCalculationBasis.Total}>{strings.tipCalculationBasisTotal}</MenuItem>
                                </Select>
                            </FormControl>
                            <Stack direction="row" spacing={2}>
                                {presets.map((p) => (
                                    <Stack key={p.idx} direction="column">
                                        <FormControlLabel
                                            control={<Radio checked={defaultPreset === p.idx} onChange={() => setDefaultPreset(p.idx)} />}
                                            label={strings.tipPresetDefault}
                                            sx={{ mb: 0.5 }}
                                        />
                                        <TextField label={p.label} type="number" value={disp(p.value)} onChange={(e) => p.set(num(e.target.value))} />
                                    </Stack>
                                ))}
                            </Stack>
                        </>
                    )}
{
    /*
<Spacer y={3} />
                    <Typography variant="subtitle1" fontWeight={700}>{strings.tipGroupTitle}</Typography>
                    <Spacer y={1} />
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                        <TextField
                            label={strings.tipGroupCount}
                            type="number"
                            value={disp(guestCountMinimumTip)}
                            onChange={(e) => {
                                const v = num(e.target.value);
                                setGuestCountMinimumTip(v);
                                // Clearing the party size turns the feature off — drop the now-inapplicable %.
                                if (v <= 0) setMinimumTipAmount(0);
                            }}
                        />
                        <TextField
                            label={strings.tipGroupAmount}
                            type="number"
                            value={disp(minimumTipAmount)}
                            onChange={(e) => setMinimumTipAmount(num(e.target.value))}
                            disabled={guestCountMinimumTip <= 0}
                            required={guestCountMinimumTip > 0}
                            error={partyTipInvalid}
                            helperText={partyTipInvalid ? strings.tipGroupAmountRequired : ' '}
                        />
                    </Stack>
    */
}
                    

                    <Spacer y={3} />
                    <Stack direction="row" spacing={2}>
                        {canUpdate && <Button variant="contained" onClick={() => save()} loading={saving} disabled={partyTipInvalid} size="input">{strings.save}</Button>}
                        {/* Brand-level tips are the base fallback and can't be removed — only group/location overrides. */}
                        {canDelete && level !== TipConfigurationScope.Brand && (
                            <Button variant="outlined" color="error" onClick={confirmDelete} disabled={!config} size="input">
                                {level === TipConfigurationScope.LocationGroup ? strings.tipRemoveGroup : strings.tipRemoveLocation}
                            </Button>
                        )}
                    </Stack>
                </Box>
            )}
        </Box>
    );
};

export default Tips;
